import React from 'react';
import './FanoutQueries.scss';
import { useTranslation } from 'react-i18next';
import { DataTable } from '../../../../../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import CustomTooltip from '../../../../../../../shared/components/common/tooltip/CustomTooltip';
import {
  FanoutQueryItem,
  PromptFanoutResponse,
} from '../../../../../../../shared/models/monitor/ResponseFeatures';
import { getMonitorTypeIcon } from '../../helpers/StatsHelper';

interface FanoutQueriesProps {
  fanout: PromptFanoutResponse | null;
  isLoading: boolean;
  // The monitored prompt's text — rows repeating it verbatim get the "Original prompt" badge.
  prompt?: string;
}

const FanoutQueries: React.FC<FanoutQueriesProps> = ({ fanout, isLoading, prompt }) => {
  const { t } = useTranslation();

  // No data yet (loading) → hide the whole section instead of a skeleton, per product decision.
  if (isLoading) {
    return null;
  }

  const queries = fanout?.items ?? [];
  const terms = fanout?.commonTerms ?? [];

  if (queries.length === 0 && terms.length === 0) {
    return null;
  }

  // Bar length = share of all analyzed fanout queries the phrase appears in (occurrences / total),
  // matching the "{count} of {total}" tooltip — not relative to the top phrase.
  const analyzedQueries = fanout?.analyzedQueries ?? 0;

  const modelBodyTemplate = (item: FanoutQueryItem) => {
    const iconClass = getMonitorTypeIcon(item.type);
    return iconClass ? <span className={`${iconClass} ico--md`} /> : null;
  };

  const normalizedPrompt = (prompt ?? '').trim().toLowerCase();

  const queryBodyTemplate = (item: FanoutQueryItem) => {
    const isOriginalPrompt =
      normalizedPrompt.length > 0 && item.query.trim().toLowerCase() === normalizedPrompt;
    return (
      <span className="fanout-queries__query">
        {item.query}
        {isOriginalPrompt && (
          <span className="fanout-queries__prompt-badge">{t('originalPromptBadge')}</span>
        )}
      </span>
    );
  };

  return (
    <div className={`fanout-queries${terms.length === 0 ? ' fanout-queries--single' : ''}`}>
      {queries.length > 0 && (
        <div className="stats__panel">
          <div className="page-box__title-action">
            <div className="page-box__title page-box__title--flex">
              {t('fanoutQueries')}
              <CustomTooltip tooltipText="fanoutQueriesTooltip" addMargin={false} />
            </div>
          </div>
          <DataTable
            value={queries}
            stripedRows
            tableClassName="table"
            className="table-wrapper table-wrapper--stack fanout-queries__table"
            responsiveLayout="stack"
            paginator={queries.length > 10}
            rows={10}
            paginatorClassName="pagination"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
          >
            <Column
              field="query"
              header={t('query')}
              headerClassName="table__header"
              bodyClassName="table__cell--stack-title"
              body={queryBodyTemplate}
            />
            <Column
              header={t('model')}
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell--center"
              style={{ width: '80px' }}
              body={modelBodyTemplate}
            />
            <Column
              field="occurrences"
              header={t('occurrences')}
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell--center"
              style={{ width: '110px' }}
            />
          </DataTable>
        </div>
      )}

      {terms.length > 0 && (
        <div className="stats__panel">
          <div className="page-box__title-action">
            <div className="page-box__title page-box__title--flex">
              {t('topPhrases')}
              <CustomTooltip tooltipText="commonTermsTooltip" addMargin={false} />
            </div>
          </div>
          <div className="stats-box">
            <div className="stats-box__content">
              <ul className="fanout-queries__terms">
                {terms.map((term, index) => {
                  const sharePct =
                    analyzedQueries > 0
                      ? Math.min(100, Math.round((term.count / analyzedQueries) * 100))
                      : 0;
                  const termRowId = `top-phrase-${index}`;
                  return (
                    <li key={term.term} id={termRowId} className="fanout-queries__term">
                      <div className="fanout-queries__term-header">
                        <span className="fanout-queries__term-label">{term.term}</span>
                        <span className="fanout-queries__term-count">{term.count}</span>
                      </div>
                      <div
                        className="fanout-queries__term-bar progress-bar--mini"
                        role="progressbar"
                        aria-label={`${term.term}: ${term.count}`}
                        aria-valuenow={sharePct}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      >
                        <span
                          className="fanout-queries__term-bar-fill"
                          style={{ width: `${sharePct}%` }}
                        />
                      </div>
                      {fanout?.analyzedQueries != null && fanout.analyzedQueries > 0 && (
                        <CustomTooltip
                          tooltipText="topPhraseBarTooltip"
                          options={{ occurrences: term.count, total: fanout.analyzedQueries }}
                          targetRef={termRowId}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FanoutQueries;
